<template>
  <Dashboard>
    <div v-if="loading">
      <svg
        width="64"
        height="64"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="animate-spin"
      >
        <circle cx="16" cy="16" r="15" stroke="#1E2733" stroke-width="2" />
        <path
          d="M16 1C17.9698 1 19.9204 1.38799 21.7403 2.14181C23.5601 2.89563 25.2137 4.00052 26.6066 5.3934C27.9995 6.78628 29.1044 8.43986 29.8582 10.2597C30.612 12.0796 31 14.0302 31 16"
          stroke="#4E54C8"
          stroke-width="2"
        />
      </svg>
    </div>
    <div v-else class="flex flex-col gap-8 w-full">
      <div class="flex gap-3 items-center">
        <div class="flex flex-col gap-3 flex-1 items-start">
          <router-link
            :to="{ name: 'RoutinesPage' }"
            class="bg-gray-800 text-text-primary text-xs px-3 py-2.5 rounded-lg flex gap-2 items-center hover:bg-gray-700 duration-300"
          >
            <img src="../../assets/icons/leftarrow.svg" :alt="$t('training.shared.alt_back')" class="w-3 h-3" />
            {{ $t('training.routine_edit.back_to_routines') }}
          </router-link>
          <div class="flex gap-4 w-full items-center">
            <div class="flex flex-col gap-3 flex-1">
              <h1 class="text-4xl font-medium text-text-primary">{{ bootcamp?.name }}</h1>
              <p class="text-text-secondary text-sm font-light">
                {{ bootcamp?.description }}
              </p>
            </div>
            <div class="flex gap-2">
              <div
                @click="toggleDeleteModal()"
                class="px-3 py-2.5 bg-danger-400 hover:bg-danger-300 duration-300 text-danger-950 text-xs font-medium rounded-lg cursor-pointer"
              >
                {{ $t('common.delete') }}
              </div>
              <div
                @click="togglePublishModal()"
                class="px-3 py-2.5 bg-primary-600 hover:bg-primary-500 duration-300 text-text-primary text-xs font-medium rounded-lg cursor-pointer"
              >
                {{ $t('training.routine_edit.review_and', { action: getPublishButtonText() }) }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex gap-6 flex-col lg:flex-row">
        <div class="flex flex-col gap-[2px] flex-[8] overflow-hidden rounded-lg">
          <h1
            class="text-[20px] py-4 px-6 flex items-center gap-3 font-medium text-text-primary tracking-[0.5px] bg-gray-900"
          >
            {{ $t('training.routine_edit.segments') }}
          </h1>
          <Container
            orientation="vertical"
            @drop="onDrop"
            class="divide-y-2 divide-gray-950"
          >
            <Draggable v-for="(item, i) in tasks" :key="item.id">
              <div
                @click="selectSegment(item)"
                class="item cursor-pointer py-4 pl-1 pr-6 flex items-center gap-1 border-l select-none"
                :key="item"
                :class="[
                  selectedSegment?.id === item.id
                    ? 'bg-gray-800 border-primary-500'
                    : 'bg-gray-900 border-gray-900',
                ]"
              >
                <img
                  src="../../assets/icons/draggable.svg"
                  :alt="$t('training.shared.alt_segment')"
                  class="w-6 h-6 select-none"
                />
                <div
                  class="flex items-center justify-center h-10 w-10 rounded-full bg-gray-800 border border-gray-700 text-sm text-text-primary"
                >
                  #{{ i + 1 }}
                </div>
                <div class="flex flex-col justify-center gap-1 pl-2">
                  <h2 class="text-text-primary">{{ item?.name }}</h2>
                  <div class="text-text-secondary flex items-center text-xs gap-2">
                    <h2>{{ item?.cs_map?.friendly_name }}</h2>
                    <span class="text-text-disabled">•</span>
                    <h2>{{ getModNameFromId(item) || item?.mod }}</h2>
                    <span class="text-text-disabled">•</span>
                    <h2>{{ $t('training.shared.minutes', item?.estimated_duration_in_minutes || 0) }}</h2>
                  </div>
                </div>
              </div>
            </Draggable>
          </Container>

          <div class="flex gap-3 items-center pt-6">
            <div class="bg-gray-800 h-px flex-1" />
            <button
              @click="createNewSegment()"
              class="h-8 w-8 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 duration-300"
            >
              <PlusIcon class="text-text-primary h-4 w-4" />
            </button>
            <div class="bg-gray-800 h-px flex-1" />
          </div>
        </div>
        <div class="flex flex-col gap-12 flex-[4]">
          <div class="flex flex-col gap-[2px]">
            <div class="flex items-center gap-3 bg-gray-900 py-4 px-6 rounded-t-lg">
              <h1
                class="text-[20px] flex items-center font-medium text-text-primary tracking-[0.5px] flex-1"
              >
                {{ selectedSegment?.name }}
              </h1>
              <div
                @click="deleteSegment(selectedSegment)"
                class="cursor-pointer h-8 w-8 rounded-md bg-gray-800 flex items-center justify-center hover:bg-gray-700 duration-300"
              >
                <img src="../../assets/icons/trash.svg" />
              </div>
              <div
                @click="updateSegment(selectedSegment)"
                class="cursor-pointer h-8 px-2 text-xs text-text-primary rounded-lg bg-primary-600 flex items-center justify-center hover:bg-primary-500 duration-300"
              >
                {{ $t('common.save') }}
              </div>
            </div>

            <div class="p-6 flex flex-col gap-6 bg-gray-900 rounded-b-lg">
              <div class="flex flex-col">
                <h1 class="text-text-primary text-sm pb-1">{{ $t('training.routine_edit.title') }}</h1>
                <input
                  v-model="selectedName"
                  class="bg-gray-800 text-text-primary tracking-[0.5px] flex-1 rounded-lg"
                />
              </div>

              <Menu as="div" class="relative inline-block text-left w-full lg:w-auto">
                <h1 class="text-text-primary text-sm pb-1">{{ $t('common.map') }}</h1>
                <MenuButton
                  class="relative w-full disabled:bg-gray-400 inline-flex items-center p-3 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
                >
                  <div class="flex-1 text-left font-light">
                    {{ selectedMap?.friendly_name || $t('training.routine_edit.select_map') }}
                  </div>
                  <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
                </MenuButton>

                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    class="absolute right-0 z-50 mt-2 w-full origin-top-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  >
                    <div class="py-1 flex flex-col px-4">
                      <MenuItem
                        :key="map"
                        v-for="map in maps"
                        v-slot="{ active }"
                        @click="updateActiveMap(map)"
                      >
                        <a
                          href="#"
                          :class="[
                            active ? 'text-text-primary' : 'text-text-secondary',
                            'group flex items-center py-2 text-sm w-full justify-start',
                          ]"
                        >
                          {{ map?.friendly_name }}
                          <CheckIcon
                            v-if="map.name === activeMap"
                            class="h-5 w-5 ml-auto text-primary-400"
                          />
                        </a>
                      </MenuItem>
                    </div>
                  </MenuItems>
                </transition>
              </Menu>

              <div class="w-full h-px bg-gray-800" />

              <Menu as="div" class="relative inline-block text-left w-full lg:w-auto">
                <h1 class="text-text-primary text-sm pb-1">{{ $t('training.routine_edit.mod') }}</h1>
                <MenuButton
                  class="relative w-full disabled:bg-gray-400 inline-flex items-center p-3 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
                >
                  <div class="flex-1 text-left font-light">
                    {{ selectedMod?.friendly_name }}
                  </div>
                  <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
                </MenuButton>

                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    class="absolute right-0 z-50 mt-2 w-full origin-top-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  >
                    <div class="py-1 flex flex-col px-4">
                      <MenuItem
                        :key="mod"
                        v-for="mod in mods"
                        v-slot="{ active }"
                        @click="updateActiveMod(mod)"
                      >
                        <a
                          href="#"
                          :class="[
                            active ? 'text-text-primary' : 'text-text-secondary',
                            'group flex items-center py-2 text-sm w-full justify-start',
                          ]"
                        >
                          {{ mod?.friendly_name }}
                        </a>
                      </MenuItem>
                    </div>
                  </MenuItems>
                </transition>
              </Menu>

              <Combobox
                v-if="selectedMod?.name === 'CreatorStudio'"
                class="flex-1"
                as="div"
                v-model="selectedCustomSet"
              >
                <div class="relative">
                  <ComboboxLabel
                    class="flex text-sm whitespace-nowrap items-center rounded-md text-text-secondary border-0 bg-gray-800 px-3 h-12 shadow-sm ring-1 ring-inset ring-gray-700 focus:ring-2 focus:ring-inset focus:ring-primary-600"
                  >
                    {{ $t('training.routine_edit.custom_set') }}
                    <ComboboxInput
                      class="appearance-none w-full bg-gray-800 border-0 ring-0 focus:ring-0 text-text-primary sm:text-sm sm:leading-6 font-medium"
                      :displayValue="(set) => set?.title"
                      @change="updateCustomSets($event.target.value)"
                      :placeholder="$t('training.routine_edit.custom_set_placeholder')"
                    />
                    <ComboboxButton
                      class="absolute inset-y-0 right-0 flex items-center rounded-r-md pr-3 focus:outline-none"
                    >
                      <img
                        src="../../assets/icons/dropdown.svg"
                        class="text-gray-400"
                        aria-hidden="true"
                      />
                    </ComboboxButton>
                  </ComboboxLabel>

                  <ComboboxOptions
                    class="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-gray-900 py-1 text-base shadow-lg ring-1 ring-gray-800 focus:outline-none sm:text-sm"
                  >
                    <ComboboxOption
                      v-for="set in customSets"
                      :key="set"
                      :value="set"
                      as="template"
                      v-slot="{ active, selected }"
                    >
                      <li
                        :class="[
                          'relative cursor-pointer select-none py-2 pl-3 pr-9',
                          selected
                            ? 'text-text-primary bg-gray-800'
                            : 'text-text-secondary hover:bg-gray-800',
                        ]"
                      >
                        <span
                          :class="[
                            'block truncate',
                            selected && 'font-semibold text-text-primary',
                          ]"
                        >
                          {{ set.title }}
                        </span>

                        <span
                          v-if="selected"
                          :class="[
                            'absolute inset-y-0 right-0 flex items-center pr-4',
                            active ? 'text-white' : 'text-indigo-600',
                          ]"
                        >
                          <CheckIcon class="h-5 w-5" aria-hidden="true" />
                        </span>
                      </li>
                    </ComboboxOption>
                  </ComboboxOptions>
                </div>
              </Combobox>

              <div class="w-full h-px bg-gray-800" />

              <div class="flex flex-col gap-1">
                <h1 class="text-text-primary text-sm pb-1">{{ $t('training.routine_edit.preset_optional') }}</h1>
                <Menu as="div" class="relative inline-block text-left w-full">
                  <MenuButton
                    class="relative w-full inline-flex h-10 items-center px-3 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-lg text-text-primary bg-gray-800 hover:bg-gray-700"
                  >
                    <span class="flex-1 text-left font-light">
                      {{
                        selectedPresetId == null || selectedPresetId === ""
                          ? $t('training.routine_edit.none')
                          : (presetsForCurrentMod.find((p) => p.id === selectedPresetId)?.friendly_name
                            || presetsForCurrentMod.find((p) => p.id === selectedPresetId)?.name)
                      }}
                    </span>
                    <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
                  </MenuButton>
                  <MenuItems
                    class="absolute right-0 z-50 mt-2 w-full max-h-48 overflow-y-auto origin-top-right rounded-lg bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  >
                    <div class="py-1 flex flex-col px-4">
                      <MenuItem v-slot="{ active }" @click="setPreset(null)">
                        <a
                          href="#"
                          :class="[
                            active ? 'text-text-primary' : 'text-text-secondary',
                            'group flex items-center py-2 text-sm w-full justify-start gap-2',
                          ]"
                        >
                          {{ $t('training.routine_edit.none') }}
                          <CheckIcon
                            v-if="selectedPresetId == null || selectedPresetId === ''"
                            class="h-5 w-5 ml-auto text-primary-400"
                          />
                        </a>
                      </MenuItem>
                      <MenuItem
                        v-for="preset in presetsForCurrentMod"
                        :key="preset.id"
                        v-slot="{ active }"
                        @click="setPreset(preset.id)"
                      >
                        <a
                          href="#"
                          :class="[
                            active ? 'text-text-primary' : 'text-text-secondary',
                            'group flex items-center py-2 text-sm w-full justify-start gap-2',
                          ]"
                        >
                          {{ preset.friendly_name || preset.name }}
                          <CheckIcon
                            v-if="selectedPresetId === preset.id"
                            class="h-5 w-5 ml-auto text-primary-400"
                          />
                        </a>
                      </MenuItem>
                    </div>
                  </MenuItems>
                </Menu>
              </div>

              <div class="w-full h-px bg-gray-800" />

              <div class="flex flex-col">
                <h1 class="text-text-primary text-sm pb-2">{{ $t('training.routine_edit.completion_type') }}</h1>
                <div class="flex flex-row bg-gray-800 p-1 rounded-md">
                  <div
                    @click="updateCompletionType('complete')"
                    class="w-full flex gap-2 text-sm items-center justify-center rounded-md p-1 cursor-pointer"
                    :class="[
                      selectedCompletionType === 'complete'
                        ? 'bg-gray-700 text-text-primary'
                        : 'text-text-secondary bg-gray-800',
                    ]"
                  >
                    {{ $t('training.routine_edit.completion') }}
                  </div>
                  <div
                    @click="updateCompletionType('time')"
                    class="w-full flex gap-2 text-sm items-center justify-center rounded-md p-1 cursor-pointer"
                    :class="[
                      selectedCompletionType === 'time'
                        ? 'bg-gray-700 text-text-primary'
                        : 'text-text-secondary bg-gray-800',
                    ]"
                  >
                    {{ $t('common.time') }}
                  </div>
                  <div
                    @click="updateCompletionType('kills')"
                    class="w-full flex gap-2 text-sm items-center justify-center rounded-md p-1 cursor-pointer"
                    :class="[
                      selectedCompletionType === 'kills'
                        ? 'bg-gray-700 text-text-primary'
                        : 'text-text-secondary bg-gray-800',
                    ]"
                  >
                    {{ $t('training.routine_edit.kills') }}
                  </div>
                  <div
                    @click="updateCompletionType('arenas')"
                    class="w-full flex gap-2 text-sm items-center justify-center rounded-md p-1 cursor-pointer"
                    :class="[
                      selectedCompletionType === 'arenas'
                        ? 'bg-gray-700 text-text-primary'
                        : 'text-text-secondary bg-gray-800',
                    ]"
                  >
                    {{ $t('training.routine_edit.arenas') }}
                  </div>
                </div>
              </div>

              <div v-if="selectedCompletionType !== 'complete'" class="flex flex-col">
                <h1 class="text-text-primary text-sm pb-2">{{ $t('training.routine_edit.completion_value') }}</h1>
                <div class="relative rounded-md shadow-sm flex flex-col">
                  <input
                    type="number"
                    v-model="selectedCompletionValue"
                    class="bg-gray-800 text-text-primary text-sm rounded-lg p-2"
                  />
                  <div
                    class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4"
                  >
                    <span
                      class="text-gray-200 sm:text-sm"
                      v-if="selectedCompletionType === 'time'"
                    >{{ $t('training.routine_edit.seconds_unit') }}</span
                    >
                    <span
                      class="text-gray-200 sm:text-sm"
                      v-else-if="selectedCompletionType === 'kills'"
                    >{{ $t('training.routine_edit.kills_unit') }}</span
                    >
                    <span
                      class="text-gray-200 sm:text-sm"
                      v-else-if="selectedCompletionType === 'arenas'"
                    >{{ $t('training.routine_edit.arenas_unit') }}</span
                    >
                  </div>
                </div>
              </div>

              <div class="w-full h-px bg-gray-800" />
              <div>
                <h1 class="text-text-primary text-sm pb-2"
                >{{ $t('training.routine_edit.estimated_duration') }}</h1
                >
                <div class="relative rounded-md shadow-sm flex flex-col">
                  <input
                    type="number"
                    v-model="selectedDuration"
                    class="bg-gray-800 text-text-primary text-sm rounded-lg p-2"
                  />
                  <div
                    class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4"
                  >
                    <span
                      class="text-gray-200 sm:text-sm"
                      id="estimated_duration-minutes"
                    >{{ $t('training.routine_edit.minutes_unit') }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dashboard>
  <StartServerModal
    :server="serverInfo"
    :show="showServerModal"
    @close="showServerModal = false"
  />
  <ModalWindow :open="showPublishModal">
    <div class="flex flex-col gap-6">
      <div class="flex gap-3 items-center">
        <h1
          class="text-text-primary text-[24px] font-semibold flex gap-3 items-center flex-1"
        >
          {{ $t('training.routine_edit.review_and', { action: getPublishButtonText() }) }}
        </h1>
        <XMarkIcon
          @click="togglePublishModal"
          class="h-6 w-6 cursor-pointer text-text-secondary"
        />
      </div>

      <div class="flex flex-col gap-2">
        <h1 class="text-text-primary flex gap-2 items-center">{{ $t('training.routine_edit.title') }} <span class="text-text-secondary text-xs">{{ $t('training.routine_edit.required') }}</span></h1>
        <input
          v-model="bootcampName"
          :placeholder="$t('training.routine_edit.title_placeholder')"
          class="bg-gray-800 text-text-primary rounded-lg p-2 text-sm"
        />
      </div>

      <div class="flex flex-col gap-2">
        <h1 class="text-text-primary flex gap-2 items-center">{{ $t('training.routine_edit.description') }} <span class="text-text-secondary text-xs">{{ $t('training.routine_edit.required') }}</span></h1>
        <textarea
          v-model="bootcampDescription"
          :placeholder="$t('training.routine_edit.description_placeholder')"
          rows="5"
          class="bg-gray-800 text-text-primary rounded-lg p-2 text-sm"
        />
      </div>

      <button
        :disabled="!bootcampName || !bootcampDescription"
        @click="publishRoutine()"
        class="bg-primary-600 text-text-primary px-4 py-2 rounded-lg hover:bg-primary-500 w-full duration-300 disabled:bg-gray-400"
      >
        {{ $t('training.routine_edit.publish_routine', { action: getPublishButtonText() }) }}
      </button>
    </div>
  </ModalWindow>
  <ModalWindow :open="showDeleteModal">
    <div class="flex flex-col gap-6">
      <div class="flex items-center gap-3">
        <h1
          class="text-text-primary text-[24px] font-semibold flex gap-3 items-center flex-1"
        >
          <img src="../../assets/icons/error.svg" :alt="$t('training.shared.alt_error_icon')" class="w-8 h-8" />
          {{ $t('training.routine_edit.delete_title', { name: bootcamp?.name }) }}
        </h1>
        <button
          @click="showDeleteModal = false"
          class="text-text-primary text-[24px] font-semibold cursor-pointer"
        >
          <XMarkIcon class="w-6 h-6 text-text-secondary" />
        </button>
      </div>

      <p class="text-text-secondary text-sm">
        {{ $t('training.routine_edit.delete_confirm') }}
      </p>
      <div class="flex gap-4">
        <button
          @click="deleteRoutine()"
          class="bg-danger-400 text-danger-950 px-4 py-2 rounded-md font-semibold hover:bg-danger-300 w-full duration-300"
        >
          {{ $t('training.routine_edit.delete_yes') }}
        </button>
        <button
          @click="showDeleteModal = false"
          class="bg-gray-800 text-text-primary px-4 py-2 rounded-md font-medium hover:bg-gray-700 w-full duration-300"
        >
          {{ $t('training.routine_edit.delete_no') }}
        </button>
      </div>
    </div>
  </ModalWindow>
</template>

<script setup>
import axios from 'axios';
import {
  ref, reactive, computed, getCurrentInstance, watch, nextTick,
} from 'vue';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useRouter, useRoute } from 'vue-router';
import { Container, Draggable } from 'vue3-smooth-dnd';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Combobox,
  ComboboxButton,
  ComboboxLabel,
  ComboboxOptions,
  ComboboxOption,
  ComboboxInput,
} from '@headlessui/vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import {
  ChevronDownIcon, PlusIcon, XMarkIcon, CheckIcon,
} from '@heroicons/vue/20/solid';
import ModalWindow from '../../components/ModalWindow.vue';
import StartServerModal from '../../components/StartServerModal.vue';
import Dashboard from '../../components/layouts/DashboardLayout.vue';

const { t } = useI18n();
const auth = useAuth();
const router = useRouter();
const route = useRoute();
const user = ref(auth.user());
const store = useStore();
const bootcamp = ref(null);
const loading = ref(true);
const serverInfo = ref();
const showServerModal = ref(false);
const activeServer = computed(() => store.getters.activeServer);
const websocket = ref(null);
const subscription = ref(null);
const instance = getCurrentInstance();
const launchLoading = ref(false);
const launchSuccess = ref(false);
const tasks = ref([]);
const selectedSegment = ref(null);
const selectedMap = ref(null);
const selectedMod = ref(null);
const selectedPresetId = ref(null);
const allPresets = ref([]);
const selectedCompletionType = ref(null);
const selectedCompletionValue = ref(null);
const selectedDuration = ref(null);
const selectedName = ref(null);
const selectedDescription = ref(null);
const showPublishModal = ref(false);
const showDeleteModal = ref(false);
const bootcampName = ref(null);
const bootcampDescription = ref(null);
const searchInput = ref('');
const customSets = ref([]);
const selectedCustomSet = ref(null);
const gameModes = ref([]);
const siteSettings = ref([]);

function getGameModes() {
  axios({
    url: '/game_modes',
    method: 'GET',
  }).then((response) => {
    gameModes.value = response.data;
  });
}

function loadSiteSettings() {
  axios.get('settings').then(({ data }) => {
    siteSettings.value = data.settings;
  });
}

getGameModes();
loadSiteSettings();

function getBootcamp() {
  loading.value = true;
  axios({
    url: `/task_lists/${route.params.slug}`,
    method: 'GET',
    params: {
      is_exam: false,
    },
  }).then((response) => {
    loading.value = false;
    bootcamp.value = response.data;
    updateNameAndDescription();
    loadTasks();
    // Use nextTick to ensure tasks are loaded and reactive state is updated
    nextTick(() => {
      if (tasks.value.length > 0) {
        selectSegment(tasks.value[0]);
      }
    });
  });
}

function updateNameAndDescription() {
  if (bootcamp.value.name !== 'User Routine') {
    bootcampName.value = bootcamp.value.name;
  }
  if (bootcamp.value.description !== 'This is a user routine') {
    bootcampDescription.value = bootcamp.value.description;
  }
}

function togglePublishModal() {
  showPublishModal.value = !showPublishModal.value;
}

function toggleDeleteModal() {
  showDeleteModal.value = !showDeleteModal.value;
}

function deleteRoutine() {
  axios({
    method: 'delete',
    url: `/task_lists/${bootcamp.value.id}`,
  }).then(() => {
    store.commit('showMessage', {
      messageType: 'success',
      messageText: t('training.routine_edit.deleted'),
    });
    setTimeout(() => {
      store.commit('hideMessage');
    }, 1500);
    router.push({ name: 'RoutinesPage' });
  }).catch((err) => {
    store.commit('showMessage', {
      messageType: 'error',
      messageText: err.response?.data?.message || t('training.routine_edit.delete_failed'),
    });
    setTimeout(() => {
      store.commit('hideMessage');
    }, 1500);
  });
}

const maps = computed(() => selectedMod.value?.cs_maps
  .filter((map) => map.cs2)
  .sort((a, b) => a.name.localeCompare(b.friendly_name))
                           || []);

function loadPresets() {
  axios.get('/presets', { headers: { 'X-TEAM-ID': store.state.activeTeam?.id } }).then((res) => {
    allPresets.value = res.data || [];
  }).catch(() => { allPresets.value = []; });
}

const presetsForCurrentMod = computed(() => {
  const modName = selectedMod.value?.name;
  if (!allPresets.value.length) return [];
  if (!modName) return allPresets.value;
  const filtered = allPresets.value.filter((p) => {
    const gm = p.game_mode || p.game_mode_id;
    const name = typeof gm === 'object' ? gm?.name : null;
    return !name || name.toLowerCase() === modName.toLowerCase();
  });
  return filtered.length ? filtered : allPresets.value;
});

function setPreset(presetIdOrNull) {
  selectedPresetId.value = presetIdOrNull ?? null;
}

const mods = computed(() => (store.state.modInfo?.mods || []).filter((mod) => mod.cs2 && mod.playable && mod.taskable));

const cs2ServerDisabled = computed(() => {
  const startCs2Server = siteSettings.value.find(
    (setting) => setting.name === 'start_cs2_server',
  )?.enabled;
  return typeof startCs2Server !== 'undefined' ? !startCs2Server : false;
});

function updateActiveMod(mod) {
  if (!mod.id) {
    selectedMod.value = store.state.modInfo.mods.find((m) => m.name.toLowerCase() === mod.toLowerCase());
  } else {
    selectedMod.value = mod;
  }

  // Reset selected map when mod changes and validate if current map is available for new mod
  if (selectedMap.value) {
    const availableMaps = selectedMod.value?.cs_maps?.filter((map) => map.cs2) || [];
    const isCurrentMapValid = availableMaps.some((map) => map.id === selectedMap.value.id);

    if (!isCurrentMapValid) {
      // Reset to first available map for this mod
      selectedMap.value = availableMaps.length > 0 ? availableMaps[0] : null;
      // If we set a new map and mod is CreatorStudio, get custom sets
      if (selectedMap.value && selectedMod.value?.name.toLowerCase() === 'creatorstudio') {
        getCustomSets();
        getCurrentSet();
      }
    } else if (selectedMod.value?.name.toLowerCase() === 'creatorstudio') {
      // Map is still valid, get custom sets if mod is CreatorStudio
      getCustomSets();
      getCurrentSet();
    }
  }
}

function getPublishButtonText() {
  return bootcamp.value.status === 'published' ? t('training.routine_edit.update') : t('training.routine_edit.publish');
}

function updateActiveMap(map) {
  // Validate that the map is available for the current mod
  if (selectedMod.value) {
    const availableMaps = selectedMod.value.cs_maps?.filter((m) => m.cs2) || [];
    const isMapValid = availableMaps.some((m) => m.id === map.id);

    if (isMapValid) {
      selectedMap.value = map;
    } else {
      console.warn(`Map ${map.friendly_name} is not available for mod ${selectedMod.value.friendly_name}`);
      return;
    }
  } else {
    selectedMap.value = map;
  }

  if (selectedMod.value?.name === 'CreatorStudio') {
    getCustomSets();
  }
}

function getCurrentSet() {
  if (!selectedSegment.value.custom_set_id) {
    searchInput.value = '';
    selectedCustomSet.value = null;
    return;
  }

  axios({
    method: 'get',
    url: `/custom_sets/${selectedSegment.value.custom_set_id}`,
    headers: {
      'X-TEAM-ID': store.state.activeTeam.id,
    },
  }).then((res) => {
    selectedCustomSet.value = res.data;
  });
}

function updateCompletionType(type) {
  selectedCompletionType.value = type;
}

function getCustomSets() {
  // Wait for selectedMap to be populated with a name before making the API call
  if (!selectedMap.value || !selectedMap.value.name) {
    return;
  }

  axios({
    method: 'get',
    url: '/custom_sets',
    params: {
      'filters[map]': selectedMap.value.name,
      search_input: searchInput.value,
      per_page: 10,
    },
    headers: {
      'X-TEAM-ID': store.state.activeTeam.id,
    },
  }).then((res) => {
    customSets.value = res.data.custom_sets;
  });
}

function updateCustomSets(value) {
  searchInput.value = value;
  getCustomSets();
}

watch(
  () => selectedCustomSet.value,
  () => {
    searchInput.value = selectedCustomSet.value?.title;
  },
);

function loadTasks() {
  // Collect all tasks from all sections and sort by step to maintain the correct order
  // The API returns tasks in the correct order based on step, so we just need to flatten across sections
  const allTasks = [];
  bootcamp.value.sections.forEach((section) => {
    section.tasks.forEach((task) => {
      allTasks.push(task);
    });
  });

  // Sort by step to maintain the order (same as RoutinePage which shows correct order)
  // This ensures tasks are ordered correctly regardless of which section they're in
  allTasks.sort((a, b) => {
    const aStep = a.step || 0;
    const bStep = b.step || 0;
    return aStep - bStep;
  });

  tasks.value = allTasks;
  if (tasks.value.length === 0) {
    createNewSegment();
  }
}

function onDrop(dropResult) {
  tasks.value = applyDrag(tasks.value, dropResult);
  // Update step values based on new order
  tasks.value.forEach((task, index) => {
    task.step = index + 1;
  });
  axios({
    method: 'put',
    url: `/task_lists/${bootcamp.value.slug}/update_tasks_order`,
    data: {
      task_ids: tasks.value.map((t) => t.id),
    },
  });
}

function createNewSegment() {
  const mod = store.state.modInfo.mods.find((m) => m.name.toLowerCase() === 'quickfire');

  // Get the first available map for the selected mod
  const availableMaps = mod?.cs_maps?.filter((map) => map.cs2) || [];
  const defaultMapId = availableMaps.length > 0 ? availableMaps[0].id : 1;

  // New segments start with no preset rather than inheriting the open segment's
  const postData = {
    task_list_id: bootcamp.value.id,
    task_list_section_id: bootcamp.value.sections[0].id,
    name: t('training.routine_edit.segment_default_name', { n: tasks.value.length + 1 }),
    cs_map_id: defaultMapId,
    mod_id: mod.id,
    estimated_duration_in_minutes: 5,
    completion_type: 'complete',
    step: tasks.value.length + 1,
    preset_id: null,
    preset_loadout_id: null,
  };
  axios({
    method: 'post',
    url: '/tasks',
    data: { task: postData },
  })
    .then((res) => {
      tasks.value.push(res.data);
      selectSegment(res.data);
    })
    .catch((err) => {
      store.commit('showMessage', {
        messageType: 'error',
        messageText: err.response.data.message,
      });
    });
}

function deleteSegment(task) {
  if (tasks.value.length === 1) {
    store.commit('showMessage', {
      messageType: 'error',
      messageText: t('training.routine_edit.last_segment'),
    });
    setTimeout(() => {
      store.commit('hideMessage');
    }, 1500);
    return;
  }
  axios({
    method: 'delete',
    url: `/tasks/${task.id}`,
  }).then(() => {
    tasks.value = tasks.value.filter((t) => t.id !== task.id);
    selectSegment(tasks.value[0]);
  });
}

function getModNameFromId(task) {
  // Find the game mode that has a custom set with this custom_set_id
  const gameMode = gameModes.value.find((gm) => gm.custom_sets.some((cs) => cs.id === task.custom_set_id));

  if (gameMode) {
    return gameMode.name;
  }

  return task.mod.charAt(0).toUpperCase() + task.mod.slice(1);
}

function updateSegment(task) {
  // Find the current index of the task to maintain order
  const currentIndex = tasks.value.findIndex((t) => t.id === task.id);
  const step = currentIndex >= 0 ? currentIndex + 1 : task.step || 1;
  const presetId = selectedPresetId.value != null && selectedPresetId.value !== ''
    ? Number(selectedPresetId.value)
    : null;
  const data = {
    name: selectedName.value,
    cs_map_id: selectedMap.value.id,
    mod_id: selectedMod.value.id,
    estimated_duration_in_minutes: selectedDuration.value,
    completion_value: selectedCompletionValue.value,
    completion_type: selectedCompletionType.value,
    step,
    preset_id: presetId,
    preset_loadout_id: presetId,
    custom_set_id:
      selectedMod.value?.name === 'CreatorStudio'
        ? selectedCustomSet.value?.id || selectedSegment.value?.custom_set_id || null
        : null,
  };
  axios({
    method: 'put',
    url: `/tasks/${task.id}`,
    data: { task: data },
  }).then((res) => {
    store.commit('showMessage', {
      messageType: 'success',
      messageText: t('training.routine_edit.segment_updated'),
    });
    setTimeout(() => {
      store.commit('hideMessage');
    }, 1500);
    tasks.value = tasks.value.map((t) => {
      if (t.id === res.data.id) {
        return res.data;
      }
      return t;
    });
  });
}

// Silent save function for auto-save (no popups)
function saveTaskSilently(task) {
  if (!task || !selectedName.value || !selectedMap.value?.id || !selectedMod.value?.id) {
    return;
  }

  // Find the current index of the task to maintain order
  const currentIndex = tasks.value.findIndex((t) => t.id === task.id);
  const step = currentIndex >= 0 ? currentIndex + 1 : task.step || 1;
  const presetId = selectedPresetId.value != null && selectedPresetId.value !== ''
    ? Number(selectedPresetId.value)
    : null;
  const data = {
    name: selectedName.value,
    cs_map_id: selectedMap.value.id,
    mod_id: selectedMod.value.id,
    estimated_duration_in_minutes: selectedDuration.value,
    completion_value: selectedCompletionValue.value,
    completion_type: selectedCompletionType.value,
    step,
    preset_id: presetId,
    preset_loadout_id: presetId,
    custom_set_id:
      selectedMod.value?.name === 'CreatorStudio'
        ? selectedCustomSet.value?.id || selectedSegment.value?.custom_set_id || null
        : null,
  };
  axios({
    method: 'put',
    url: `/tasks/${task.id}`,
    data: { task: data },
  }).then((res) => {
    // Update the task in the list silently
    tasks.value = tasks.value.map((t) => {
      if (t.id === res.data.id) {
        return res.data;
      }
      return t;
    });
  }).catch(() => {
    // Silently handle errors
  });
}

function publishRoutine() {
  const published = bootcamp.value.status === 'published';
  axios({
    method: 'put',
    url: `/task_lists/${bootcamp.value.id}`,
    data: {
      name: bootcampName.value,
      description: bootcampDescription.value,
      status: 'published',
    },
  }).then(() => {
    if (published) {
      store.commit('showMessage', {
        messageType: 'success',
        messageText: t('training.routine_edit.updated'),
      });
    } else {
      store.commit('showMessage', {
        messageType: 'success',
        messageText: t('training.routine_edit.published'),
      });
    }
    router.push({ name: 'RoutinePage', params: { slug: bootcamp.value.slug } });
  });
}

function selectSegment(task) {
  // Auto-save the current segment before switching
  if (selectedSegment.value && selectedSegment.value.id !== task.id) {
    saveTaskSilently(selectedSegment.value);
  }

  // Check to make sure that the selected Custom Set ID is not a certain game mode
  const gameMode = gameModes.value.find((gm) => gm.custom_sets.some((cs) => cs.id === task.custom_set_id));

  if (gameMode) {
    task.mod = gameMode.name;
  }

  selectedSegment.value = task;

  // Update mod first, then map to ensure proper validation
  updateActiveMod(task.mod);

  // Only update map if it's valid for the selected mod
  if (task.cs_map && selectedMod.value) {
    const availableMaps = selectedMod.value.cs_maps?.filter((m) => m.cs2) || [];
    const isMapValid = availableMaps.some((m) => m.id === task.cs_map.id);

    if (isMapValid) {
      updateActiveMap(task.cs_map);
    } else {
      // If the task's map is not valid for the mod, select the first available map
      selectedMap.value = availableMaps.length > 0 ? availableMaps[0] : null;
      // If we set a map and mod is CreatorStudio, get custom sets
      if (selectedMap.value && selectedMod.value?.name.toLowerCase() === 'creatorstudio') {
        getCustomSets();
        getCurrentSet();
      }
    }
  } else if (!task.cs_map && selectedMod.value) {
    // If task has no map, set the first available map for the mod
    const availableMaps = selectedMod.value.cs_maps?.filter((m) => m.cs2) || [];
    selectedMap.value = availableMaps.length > 0 ? availableMaps[0] : null;
    // If we set a map and mod is CreatorStudio, get custom sets
    if (selectedMap.value && selectedMod.value?.name.toLowerCase() === 'creatorstudio') {
      getCustomSets();
      getCurrentSet();
    }
  }

  selectedCompletionType.value = task.completion_type;
  selectedDuration.value = task.estimated_duration_in_minutes;
  selectedCompletionValue.value = task.completion_value;
  selectedName.value = task.name;
  selectedPresetId.value = task.preset_id ?? task.preset?.id ?? null;
}

function applyDrag(arr, dragResult) {
  const { removedIndex, addedIndex, payload } = dragResult;

  if (removedIndex === null && addedIndex === null) return arr;
  const result = [...arr];
  let itemToAdd = payload;

  if (removedIndex !== null) {
    itemToAdd = result.splice(removedIndex, 1)[0];
  }
  if (addedIndex !== null) {
    result.splice(addedIndex, 0, itemToAdd);
  }
  return result;
}

loadPresets();
getBootcamp();
</script>

<style>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type=number] {
  -moz-appearance: textfield;
}
</style>
